import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, X, CreditCard, FileUp, Loader2, AlertTriangle, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, cur = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

function Modal({ title, children, onClose, wide }) {
  return <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
    <div className={`modal ${wide ? "modal-wide" : ""}`} onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>;
}
function Field({ label, children }) { return <label className="field"><span>{label}</span>{children}</label>; }

export default function CreditCardsPage({ company, companies, user }) {
  const canEdit = user?.role !== "viewer";
  const [cards, setCards] = useState([]);
  const [dash, setDash] = useState(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [showDebtForm, setShowDebtForm] = useState(false);
  const load = async () => {
    const [c, d] = await Promise.all([
      axios.get(`${API}/credit-cards`, { params: { company_id: company } }),
      axios.get(`${API}/credit-cards/dashboard`),
    ]);
    setCards(c.data); setDash(d.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company]);

  const removeCard = async (c) => {
    if (!window.confirm(`${c.bank} / ${c.card_name || c.owner_name} silinsin mi?`)) return;
    await axios.delete(`${API}/credit-cards/${c.id}`); load();
  };

  // Group by bank
  const grouped = cards.reduce((acc, c) => {
    (acc[c.bank] ||= []).push(c); return acc;
  }, {});

  return <div className="page">
    <section className="page-toolbar">
      <div>
        <p className="eyebrow">KREDİ KARTLARI</p>
        <h2>{dash?.card_count || 0} Kart · {Object.keys(grouped).length} Banka</h2>
        <p className="muted">Hesap kesim ve son ödeme tarihleri 3 gün öncesinden ödeme takviminde hatırlatılır.</p>
      </div>
      {canEdit && <div className="toolbar-actions">
        <button className="outline-btn" data-testid="card-report-debt-btn" onClick={() => setShowDebtForm(true)}>
          <TrendingDown size={16} /> Kart borcu bildir
        </button>
        <button className="primary-btn" data-testid="card-new-btn" onClick={() => setShowCardForm(true)}>
          <Plus size={17} /> Yeni kart
        </button>
      </div>}
    </section>

    {dash && <section className="metric-grid" data-testid="card-dashboard">
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><CreditCard size={13} /> Toplam Limit</span></div>
        <div className="metric-value">{money(dash.total_limit_try)}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><TrendingDown size={13} /> Toplam Borç</span></div>
        <div className="metric-value" style={{ color: "#dc2626" }}>{money(dash.total_debt_try)}</div>
        <div className="muted" style={{ fontSize: 12 }}>Kullanım %{dash.utilization_pct}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label">Toplam Asgari</span></div>
        <div className="metric-value">{money(dash.total_min_try)}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label">Kullanılabilir Limit</span></div>
        <div className="metric-value" style={{ color: "#16a34a" }}>{money(dash.available_try)}</div>
      </div>
    </section>}

    <section className="panel full-panel">
      <div className="table-scroll"><table>
        <thead><tr>
          <th>TİP</th><th>AD / UNVAN</th><th>BANKA / KART</th><th>LİMİT</th>
          <th>HESAP KESİM</th><th>SON ÖDEME</th><th>ASGARİ</th><th>TOPLAM BORÇ</th><th></th>
        </tr></thead>
        <tbody>
          {cards.length === 0 && <tr><td colSpan="9" style={{ textAlign: "center", padding: 24 }} className="muted">Henüz kayıtlı kart yok. "Yeni kart" ile ekleyin.</td></tr>}
          {Object.entries(grouped).map(([bank, items]) => <Fragment key={bank}>
            <tr style={{ background: "#f8fafc" }}><td colSpan="9" style={{ fontWeight: 600, color: "#1e40af" }}>{bank} ({items.length})</td></tr>
            {items.map(c => <tr key={c.id} data-testid={`card-row-${c.id}`}>
              <td><span className="company-pill" style={{ background: c.owner_type === "corporate" ? "#dbeafe" : "#fef3c7", color: c.owner_type === "corporate" ? "#1e40af" : "#92400e" }}>
                {c.owner_type === "corporate" ? "Tüzel" : "Şahıs"}
              </span></td>
              <td><strong>{c.owner_name}</strong>{c.card_name && <span>{c.card_name}</span>}</td>
              <td>{c.bank}{c.card_name && <span>{c.card_name}</span>}</td>
              <td>{money(c.limit, c.currency)}</td>
              <td>Her ayın {c.cut_day}'i</td>
              <td>{c.current_due_date || `Her ayın ${c.due_day}'i`}</td>
              <td>{c.current_min ? money(c.current_min, c.currency) : "—"}</td>
              <td style={{ color: (c.current_debt || 0) > 0 ? "#dc2626" : "inherit", fontWeight: 600 }}>
                {c.current_debt ? money(c.current_debt, c.currency) : "—"}
              </td>
              <td>{user?.role === "admin" && <button className="icon-btn" data-testid={`card-del-${c.id}`} onClick={() => removeCard(c)}><Trash2 size={13} /></button>}</td>
            </tr>)}
          </Fragment>)}
        </tbody>
      </table></div>
    </section>

    {showCardForm && <CardForm companies={companies} onClose={() => setShowCardForm(false)} onDone={() => { setShowCardForm(false); load(); }} />}
    {showDebtForm && <DebtForm cards={cards} onClose={() => setShowDebtForm(false)} onDone={() => { setShowDebtForm(false); load(); }} />}
  </div>;
}

function CardForm({ companies, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    owner_type: "corporate", owner_name: "", bank: "", card_name: "",
    limit: "", currency: "TRY", cut_day: 15, due_day: 28,
    company_id: companies?.[0]?.id || "", notes: "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${API}/credit-cards`, {
        ...f, limit: parseFloat(f.limit), cut_day: parseInt(f.cut_day), due_day: parseInt(f.due_day),
      });
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };
  if (step === 1) {
    return <Modal onClose={onClose} title="Kart tipi seçin">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: 20 }}>
        <button type="button" data-testid="card-type-corporate" onClick={() => { setF({ ...f, owner_type: "corporate" }); setStep(2); }}
          style={{ padding: 24, borderRadius: 10, border: "2px solid #e5e7eb", background: "white", cursor: "pointer", textAlign: "left" }}>
          <CreditCard size={32} color="#1e40af" />
          <h4 style={{ margin: "12px 0 4px" }}>Tüzel</h4>
          <p className="muted" style={{ fontSize: 13 }}>Şirket kartı · Atlas Holding, Rota Lojistik vs.</p>
        </button>
        <button type="button" data-testid="card-type-personal" onClick={() => { setF({ ...f, owner_type: "personal" }); setStep(2); }}
          style={{ padding: 24, borderRadius: 10, border: "2px solid #e5e7eb", background: "white", cursor: "pointer", textAlign: "left" }}>
          <CreditCard size={32} color="#f59e0b" />
          <h4 style={{ margin: "12px 0 4px" }}>Şahıs</h4>
          <p className="muted" style={{ fontSize: 13 }}>Çalışan ya da ortak adına kayıtlı kart</p>
        </button>
      </div>
    </Modal>;
  }
  return <Modal onClose={onClose} title={`Yeni ${f.owner_type === "corporate" ? "tüzel" : "şahıs"} kart`}>
    <form onSubmit={submit} className="form">
      <Field label={f.owner_type === "corporate" ? "Şirket ünvanı" : "Kart sahibi adı"}>
        <input data-testid="card-form-owner" value={f.owner_name} onChange={e => setF({ ...f, owner_name: e.target.value })} required />
      </Field>
      <div className="row2">
        <Field label="Banka"><input data-testid="card-form-bank" value={f.bank} onChange={e => setF({ ...f, bank: e.target.value })} required placeholder="İş Bankası" /></Field>
        <Field label="Kart adı (ops.)"><input data-testid="card-form-cardname" value={f.card_name} onChange={e => setF({ ...f, card_name: e.target.value })} placeholder="Platinum · ****1234" /></Field>
      </div>
      <div className="row2">
        <Field label="Kart limiti"><input data-testid="card-form-limit" type="number" step="0.01" value={f.limit} onChange={e => setF({ ...f, limit: e.target.value })} required /></Field>
        <Field label="Para birimi"><select data-testid="card-form-currency" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
          {["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}
        </select></Field>
      </div>
      <div className="row2">
        <Field label="Hesap kesim günü (1-31)"><input data-testid="card-form-cut" type="number" min="1" max="31" value={f.cut_day} onChange={e => setF({ ...f, cut_day: e.target.value })} required /></Field>
        <Field label="Son ödeme günü (1-31)"><input data-testid="card-form-due" type="number" min="1" max="31" value={f.due_day} onChange={e => setF({ ...f, due_day: e.target.value })} required /></Field>
      </div>
      <Field label="Şirket"><select data-testid="card-form-company" value={f.company_id} onChange={e => setF({ ...f, company_id: e.target.value })}>
        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select></Field>
      <div className="muted" style={{ padding: 10, background: "#eff6ff", borderRadius: 6, fontSize: 12 }}>
        <AlertTriangle size={13} style={{ display: "inline", marginRight: 4 }} />
        Hesap kesim ve son ödeme için 3 gün önce otomatik hatırlatma oluşturulacak (Ödeme Takvimi'nde görünür).
      </div>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={() => setStep(1)}>Geri</button>
        <button data-testid="card-form-submit" className="primary-btn" disabled={busy}>{busy ? "..." : "Kaydet"}</button>
      </div>
    </form>
  </Modal>;
}

function DebtForm({ cards, onClose, onDone }) {
  const [f, setF] = useState({
    card_id: cards[0]?.id || "", debt_amount: "", min_payment: "",
    due_date: new Date(Date.now() + 20 * 86400000).toISOString().slice(0, 10),
    currency: "TRY", period: "",
  });
  const [parsing, setParsing] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const uploadPdf = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    setParsing(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/credit-cards/parse-statement-pdf`, form);
      setAttachment({ id: data.attachment_id, filename: data.filename });
      setF(prev => ({
        ...prev,
        debt_amount: data.debt_amount ?? prev.debt_amount,
        min_payment: data.min_payment ?? prev.min_payment,
        due_date: data.due_date || prev.due_date,
        currency: data.currency || prev.currency,
        period: data.period || prev.period,
      }));
    } catch (ex) { setErr(ex.response?.data?.detail || "PDF parse hatası"); }
    finally { setParsing(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await axios.post(`${API}/credit-cards/statement`, {
        card_id: f.card_id,
        debt_amount: parseFloat(f.debt_amount),
        min_payment: parseFloat(f.min_payment),
        due_date: f.due_date,
        currency: f.currency,
        period: f.period,
        attachment_id: attachment?.id,
      });
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };

  if (cards.length === 0) {
    return <Modal onClose={onClose} title="Kart borcu bildir">
      <div className="muted" style={{ padding: 20, textAlign: "center" }}>Henüz kayıtlı kart yok. Önce "Yeni kart" ile en az bir kart ekleyin.</div>
    </Modal>;
  }
  return <Modal onClose={onClose} title="Kart borcu bildir">
    <form onSubmit={submit} className="form">
      <Field label="Kart seç">
        <select data-testid="debt-form-card" value={f.card_id} onChange={e => setF({ ...f, card_id: e.target.value })} required>
          {cards.map(c => <option key={c.id} value={c.id}>{c.bank} · {c.card_name || c.owner_name} ({money(c.limit, c.currency)})</option>)}
        </select>
      </Field>
      <label className="outline-btn" style={{ cursor: parsing ? "wait" : "pointer" }} data-testid="debt-form-pdf">
        {parsing ? <><Loader2 size={14} className="spin" /> PDF okunuyor...</> : <><FileUp size={14} /> {attachment ? `✓ ${attachment.filename}` : "PDF ekstre yükle (otomatik oku)"}</>}
        <input type="file" accept=".pdf" hidden onChange={uploadPdf} disabled={parsing} />
      </label>
      <div className="row2">
        <Field label="Toplam borç"><input data-testid="debt-form-amount" type="number" step="0.01" value={f.debt_amount} onChange={e => setF({ ...f, debt_amount: e.target.value })} required /></Field>
        <Field label="Para birimi"><select data-testid="debt-form-currency" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
          {["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}
        </select></Field>
      </div>
      <div className="row2">
        <Field label="Asgari ödeme"><input data-testid="debt-form-min" type="number" step="0.01" value={f.min_payment} onChange={e => setF({ ...f, min_payment: e.target.value })} required /></Field>
        <Field label="Son ödeme tarihi"><input data-testid="debt-form-duedate" type="date" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} required /></Field>
      </div>
      <Field label="Dönem (opsiyonel)"><input data-testid="debt-form-period" value={f.period} onChange={e => setF({ ...f, period: e.target.value })} placeholder="2026-02" /></Field>
      <div className="muted" style={{ padding: 10, background: "#fef3c7", borderRadius: 6, fontSize: 12, color: "#92400e" }}>
        <AlertTriangle size={13} style={{ display: "inline", marginRight: 4 }} />
        Son ödeme tarihinden 3 gün önce Ödeme Takvimi'nde otomatik hatırlatma oluşturulacak.
      </div>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button data-testid="debt-form-submit" className="primary-btn" disabled={busy}>{busy ? "..." : "Borcu kaydet"}</button>
      </div>
    </form>
  </Modal>;
}
